/**
 * Configurazione d'ambiente, validata all'avvio.
 *
 * Le variabili con prefisso `VITE_` finiscono nel bundle e sono quindi
 * **pubbliche**. Nessun valore privilegiato può comparire qui: la service-role
 * key di Supabase non deve mai raggiungere il browser. Le operazioni che la
 * richiedono vivono nelle Edge Function.
 */

import { z } from 'zod';

const envSchema = z.object({
  /**
   * Indirizzo del progetto, es. https://abcdefgh.supabase.co. Il pannello di
   * Supabase lo mostra anche con il percorso `/rest/v1/` (pagina Data API): se
   * copiato così, il client costruirebbe indirizzi inesistenti come
   * `/rest/v1/auth/v1/authorize`. Si conserva quindi solo l'origine.
   */
  VITE_SUPABASE_URL: z
    .string()
    .trim()
    .url()
    .transform((value) => new URL(value).origin)
    .optional(),
  VITE_SUPABASE_ANON_KEY: z.string().min(20).optional(),
  /**
   * Modalità dimostrativa: PostgreSQL in WebAssembly nel browser, con le stesse
   * migrazioni e lo stesso seed della produzione. Consente di provare Bacco
   * senza alcun servizio esterno.
   */
  VITE_DEMO_MODE: z
    .enum(['true', 'false'])
    .optional()
    .transform((value) => value === 'true'),
  /**
   * Mostra «Accedi con Google». Va attivato solo dopo aver abilitato il provider
   * Google in Supabase (vedi docs/DEPLOY.md): un pulsante che porta a un errore
   * è peggio di nessun pulsante.
   */
  VITE_AUTH_GOOGLE: z
    .enum(['true', 'false'])
    .optional()
    .transform((value) => value === 'true'),
  VITE_APP_ENV: z.enum(['development', 'staging', 'production']).optional().default('development'),
  VITE_SENTRY_DSN: z.string().optional(),
});

export type AppEnv = z.infer<typeof envSchema>;

function readEnv(): AppEnv {
  const parsed = envSchema.safeParse(import.meta.env);
  if (!parsed.success) {
    const issues = parsed.error.issues.map((issue) => `${issue.path.join('.')}: ${issue.message}`);
    throw new Error(`Configurazione d'ambiente non valida:\n${issues.join('\n')}`);
  }
  return parsed.data;
}

export const env = readEnv();

/**
 * Bacco usa la modalità dimostrativa se richiesta esplicitamente **oppure** se
 * mancano le credenziali Supabase. Il secondo caso evita la schermata bianca a
 * chi clona il repository e lancia `npm run dev` senza configurare nulla.
 */
export const isDemoMode: boolean =
  env.VITE_DEMO_MODE === true || !env.VITE_SUPABASE_URL || !env.VITE_SUPABASE_ANON_KEY;

/** L'accesso con Google esiste solo con Supabase: nella demo non ci sono account reali. */
export const isGoogleSignInEnabled: boolean = !isDemoMode && env.VITE_AUTH_GOOGLE === true;

export const isProduction: boolean = env.VITE_APP_ENV === 'production';

/** Verifica che una configurazione di produzione sia completa e coerente. */
export function assertProductionConfig(): void {
  if (!isProduction) return;
  if (isDemoMode) {
    throw new Error(
      'La modalità dimostrativa non può essere attiva in produzione: configura VITE_SUPABASE_URL e VITE_SUPABASE_ANON_KEY.',
    );
  }
}
